import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { translateExamLabel, prettifyGeneratedName } from '../utils/examNames';
import './ExamPage.css';

const fmtTime = (s) => {
  if (s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

// Enunciado de un supuesto práctico: las preguntas de caso no se pueden contestar sin él.
const CaseStatement = ({ title, text }) => (
  <section className="case-statement">
    {title && <h2 className="case-statement-title">{title}</h2>}
    <p className="case-statement-text">{text}</p>
  </section>
);

const ExamPage = () => {
  const { exam_type, exam_name } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Desde la lista de exámenes oficiales llega el título con su convocatoria ("Septiembre 2024 · Ejercicio 1 (Test)");
  // con un enlace directo no hay, y se usa el nombre del examen.
  const displayName = exam_type === 'generado'
    ? prettifyGeneratedName(decodeURIComponent(exam_name))
    : exam_type === 'oficial'
      ? (location.state?.title || translateExamLabel(decodeURIComponent(exam_name)))
      : decodeURIComponent(exam_name);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  // Modo: null = pantalla inicial, 'libre' o 'examen'
  const [mode, setMode] = useState(null);
  const [examMinutes, setExamMinutes] = useState(60);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);
  // Evita que el tracking de respuestas se dispare más de una vez por examen
  // completado. Antes el POST vivía dentro del render de "showResults" (ver
  // el useEffect de tracking más abajo) y cualquier re-render repetía el
  // lote entero, duplicando attempts/box en el grafo.
  const trackedRef = useRef(false);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const url = (exam_type === 'oficial' || exam_type === 'generado')
          ? `/api/exam/by-name/${exam_name}`
          : `/api/exam/${exam_type}/${exam_name}`;
        const r = await axios.get(url);
        // Normalizar opciones a {text, image, key}. Algunos endpoints devuelven strings,
        // otros devuelven objetos. La `key` es la cadena que usamos para comparar selección.
        const normOpt = (opt) => {
          if (typeof opt === 'string') return { text: opt, image: null, key: opt };
          const text = opt?.text || '';
          const image = opt?.image || null;
          return { text, image, key: image || text };
        };
        const normalized = r.data.map((q) => {
          const options = (q.options || []).map(normOpt);
          let answer = q.answer;
          if (typeof answer === 'string') answer = { text: answer, image: null, key: answer };
          else if (answer && typeof answer === 'object') answer = normOpt(answer);
          return { ...q, options, answer };
        });
        setQuestions(normalized);
      } catch (error) {
        console.error('Error fetching questions', error);
      } finally {
        setLoading(false);
      }
    };
    trackedRef.current = false; // examen nuevo: puede volver a registrarse al terminar
    fetchQuestions();
  }, [exam_type, exam_name]);

  // Timer
  useEffect(() => {
    if (mode === 'examen' && timeLeft !== null && !showResults) {
      timerRef.current = setInterval(() => {
        setTimeLeft((t) => {
          if (t <= 1) {
            clearInterval(timerRef.current);
            setShowResults(true);
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [mode, timeLeft, showResults]);

  // Tracking de respuestas: se dispara una sola vez por examen completado,
  // en un efecto (no durante el render). Antes vivía dentro del bloque
  // `if (showResults)` del render de resultados: cualquier re-render con los
  // resultados visibles volvía a lanzar el POST por cada pregunta, duplicando
  // attempts/box en el grafo. Ahora además se manda en un solo lote.
  useEffect(() => {
    if (!showResults || trackedRef.current || exam_type !== 'oficial') return;
    trackedRef.current = true;
    const answers = questions
      .map((q, i) => ({ q, i, selected: selectedAnswers[i] }))
      .filter((x) => x.selected)
      .map((x) => ({
        exam_name: decodeURIComponent(exam_name),
        question_number: x.q.number || (x.i + 1),
        correct: x.selected === x.q.answer?.key,
      }));
    if (answers.length) {
      axios.post('/api/track/answered-batch', { answers }).catch(() => {});
    }
  }, [showResults, exam_type, exam_name, questions, selectedAnswers]);

  const startMode = (m) => {
    setMode(m);
    setCurrentQuestionIndex(0);
    setSelectedAnswers({});
    setShowResults(false);
    trackedRef.current = false;
    if (m === 'examen') setTimeLeft(examMinutes * 60);
  };

  const restart = () => {
    setMode(null);
    setShowResults(false);
    setSelectedAnswers({});
    setCurrentQuestionIndex(0);
    setTimeLeft(null);
    trackedRef.current = false;
  };

  const handleAnswerSelect = (idx, answer) => {
    setSelectedAnswers({ ...selectedAnswers, [idx]: answer });
  };

  const calculateScore = () => {
    let score = 0;
    questions.forEach((q, i) => {
      if (selectedAnswers[i] === q.answer?.key) score++;
    });
    return score;
  };

  if (loading) return <div className="page-loader">Cargando examen…</div>;

  if (questions.length === 0) {
    return (
      <div>
        <button onClick={() => navigate(-1)} className="back-link">← Volver</button>
        <div className="empty-state"><p>No se encontraron preguntas para este examen.</p></div>
      </div>
    );
  }

  // ===== Pantalla inicial: elegir modo =====
  if (mode === null) {
    return (
      <div className="exam-page">
        <button onClick={() => navigate(-1)} className="back-link">← Volver</button>
        <header className="page-header">
          <h1 className="page-title">{displayName}</h1>
          <p className="page-subtitle">{questions.length} preguntas</p>
        </header>
        <div className="mode-cards">
          <div className="mode-card" onClick={() => startMode('libre')}>
            <div className="mode-icon">📖</div>
            <h3>Modo libre</h3>
            <p>Sin tiempo. Cambia entre preguntas a tu ritmo. Sin corrección hasta finalizar.</p>
            <button className="btn btn-secondary">Empezar libre</button>
          </div>
          <div className="mode-card mode-card-primary">
            <div className="mode-icon">⏱️</div>
            <h3>Modo examen real</h3>
            <p>Temporizado, sin pausa. Al acabar el tiempo o pulsar finalizar se muestra la corrección con explicaciones.</p>
            <div className="form-group">
              <label>Tiempo (minutos)</label>
              <input
                type="number"
                value={examMinutes}
                onChange={(e) => setExamMinutes(parseInt(e.target.value, 10) || 60)}
                min="5"
                max="240"
              />
            </div>
            <button className="btn btn-primary" onClick={() => startMode('examen')}>
              Empezar examen ({examMinutes} min)
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Resultados =====
  if (showResults) {
    const score = calculateScore();
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="exam-results">
        <header className="page-header">
          <h1 className="page-title">Resultados</h1>
          <p className="page-subtitle">{displayName} · {mode === 'examen' ? 'Modo examen' : 'Modo libre'}</p>
        </header>

        <div className="card results-card">
          <div className="results-score">
            <div className="results-pct">{pct}%</div>
            <div className="results-detail">
              {score} de {questions.length} preguntas correctas
            </div>
          </div>
          <div className="results-actions">
            <button onClick={() => navigate(-1)} className="btn btn-secondary">Volver</button>
            <button onClick={restart} className="btn btn-primary">Repetir</button>
          </div>
        </div>

        {/* Revisión detallada */}
        <h3 className="section-title" style={{ marginTop: 28 }}>Revisión</h3>
        <div className="review-list">
          {questions.map((q, i) => {
            const selected = selectedAnswers[i];
            const correctKey = q.answer?.key;
            const isOk = selected === correctKey;
            const isAnswered = !!selected;
            // El enunciado de un supuesto se muestra una vez, antes de su primera pregunta
            const newCase = q.case_text && q.case_text !== questions[i - 1]?.case_text;
            return (
              <React.Fragment key={i}>
              {newCase && <CaseStatement title={q.case_title} text={q.case_text} />}
              <div className={`review-item ${isOk ? 'ok' : isAnswered ? 'ko' : 'skipped'}`}>
                <div className="review-header">
                  <span className="review-num">#{i + 1}</span>
                  <span className="review-status">
                    {isOk ? '✓ correcta' : isAnswered ? '✗ incorrecta' : '— sin responder'}
                  </span>
                </div>
                <div className="review-q">{q.text}</div>
                <ul className="review-options">
                  {q.options.map((opt, j) => {
                    const isCorrect = opt.key === correctKey;
                    const isSelected = opt.key === selected;
                    let cls = '';
                    if (isCorrect) cls = 'correct';
                    else if (isSelected && !isCorrect) cls = 'wrong';
                    return (
                      <li key={j} className={cls}>
                        {opt.text && <span>{opt.text}</span>}
                        {opt.image && (
                          <img
                            src={opt.image}
                            alt={`Opción ${String.fromCharCode(65 + j)}`}
                            style={{ display: 'block', maxWidth: 280, maxHeight: 80, marginTop: 4 }}
                          />
                        )}
                        {isCorrect && <span className="tag-ok"> respuesta correcta</span>}
                        {isSelected && !isCorrect && <span className="tag-ko"> tu respuesta</span>}
                      </li>
                    );
                  })}
                </ul>
                {q.explanation && (
                  <div className="review-explanation">
                    <p className="review-explanation-text">{q.explanation}</p>
                    {q.justification && (
                      <blockquote className="review-citation">{q.justification}</blockquote>
                    )}
                  </div>
                )}
              </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  }

  // ===== Quiz en marcha =====
  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const answered = Object.keys(selectedAnswers).length;

  return (
    <div className="exam-page">
      <button onClick={() => { if (window.confirm('¿Salir del examen? Perderás el progreso.')) restart(); }} className="back-link">
        ← Salir
      </button>

      <header className="page-header exam-header-row">
        <div>
          <h1 className="page-title">{displayName}</h1>
          <p className="page-subtitle">
            Pregunta {currentQuestionIndex + 1} de {questions.length} · {answered} respondida{answered === 1 ? '' : 's'}
          </p>
        </div>
        {mode === 'examen' && (
          <div className={`exam-timer ${timeLeft < 300 ? 'urgent' : ''}`}>
            ⏱️ {fmtTime(timeLeft)}
          </div>
        )}
      </header>

      <div className="exam-progress-bar">
        <div className="exam-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {currentQuestion.case_text && (
        <CaseStatement title={currentQuestion.case_title} text={currentQuestion.case_text} />
      )}

      <div className="card question-card">
        <p className="question-text">{currentQuestion.text}</p>

        <ul className="answers-list">
          {currentQuestion.options.map((option, idx) => {
            const checked = selectedAnswers[currentQuestionIndex] === option.key;
            return (
              <li
                key={idx}
                className={`answer-row ${checked ? 'selected' : ''}`}
                onClick={() => handleAnswerSelect(currentQuestionIndex, option.key)}
              >
                <input
                  type="radio"
                  name={`question-${currentQuestionIndex}`}
                  value={option.key}
                  onChange={() => handleAnswerSelect(currentQuestionIndex, option.key)}
                  checked={checked}
                />
                <span style={{ flex: 1 }}>
                  {option.text && <span>{option.text}</span>}
                  {option.image && (
                    <img
                      src={option.image}
                      alt={`Opción ${String.fromCharCode(65 + idx)}`}
                      style={{ display: 'block', maxWidth: 320, maxHeight: 120, marginTop: 6 }}
                    />
                  )}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="question-actions">
          <button
            type="button"
            onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
            disabled={currentQuestionIndex === 0}
            className="btn btn-secondary"
          >
            Anterior
          </button>
          {currentQuestionIndex < questions.length - 1 ? (
            <button
              type="button"
              onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              className="btn btn-primary"
            >
              Siguiente
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowResults(true)}
              className="btn btn-primary"
            >
              Finalizar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamPage;
